'use client';

import { useRouter } from 'next/navigation';
import { useTransition, useState } from 'react';
import { useLocalizedSlug } from '@/lib/i18n/useLocalizedSlug';
import type { Lang } from '@/proxy';

interface LanguageSwitcherProps {
  locale: string;
  className?: string;
}

export function LanguageSwitcher({ locale, className = '' }: LanguageSwitcherProps) {
  const router    = useRouter();
  const { getTranslatedPath } = useLocalizedSlug();
  const [pending, startTransition] = useTransition();
  const [resolving, setResolving] = useState(false);

  const busy = pending || resolving;

  const toggle = async () => {
    if (busy) return;
    const next = locale === 'en' ? 'ar' : 'en';
    setResolving(true);
    try {
      // Slugged routes (blog/services) resolve the target language's slug via the
      // API; everything else just swaps the locale prefix.
      const newPath = await getTranslatedPath(next as Lang);
      startTransition(() => router.push(newPath));
    } finally {
      setResolving(false);
    }
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      aria-label={locale === 'en' ? 'Switch to Arabic' : 'Switch to English'}
      className={[
        'flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm font-medium',
        'border border-(--border) text-(--muted)',
        'hover:border-primary-500 hover:text-primary-500',
        'transition-colors duration-180 cursor-pointer',
        busy ? 'opacity-50 cursor-not-allowed' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="font-mono text-xs leading-none">
        {locale === 'en' ? 'ع' : 'EN'}
      </span>
    </button>
  );
}

export default LanguageSwitcher;
